/**
 * DANGER - how bad the fight is, in four steps. Combat's livery follows it.
 *
 * Computed from who still stands against the party:
 *
 *   - the HIGHEST Codex tier among the opposition still in the fight sets
 *     the step (the Codex is the app's own yardstick, `threatAssessment`);
 *   - being OUTNUMBERED two to one raises it by one;
 *   - the GM's PIN outranks the numbers.
 *
 * The opposition is everyone in the fight who is not on the party's team.
 * With no party named there is no way to tell the sides apart, so nobody is
 * counted and the step is the lowest - the GM names the party once, or pins.
 *
 * A horde counts as one: its size is already in its tier (the MASS epithet).
 *
 * Pure, zero React dependency.
 */
import { classifyThreat, TIER_ORDER } from '../threatAssessment';
import type { ThreatTier } from '../threatAssessment';
import { projectDefender } from '../roster/projection';
import { sidesOf } from './sides';
import type { Combatant } from '../roster/types';
import type { PhaseFrame } from './types';

export const DANGER_STEPS = [1, 2, 3, 4] as const;
export type DangerStep = (typeof DANGER_STEPS)[number];

export const DANGER_LABELS: Record<DangerStep, string> = {
  1: 'Scuffle',
  2: 'Fight',
  3: 'Hard fight',
  4: 'Mortal danger',
};

export const isDangerStep = (value: unknown): value is DangerStep => DANGER_STEPS.includes(value as DangerStep);

const STEP_OF_TIER: Record<ThreatTier, DangerStep> = {
  MINIMA: 1,
  MINORIS: 1,
  MEDIA: 2,
  MAJORIS: 3,
  EXTREMIS: 4,
  TERMINUS: 4,
  ULTIMA: 4,
};

const clampStep = (n: number): DangerStep => Math.max(1, Math.min(4, n)) as DangerStep;

/** The step for a party of `partyInTheFight` facing opposition of these tiers. Nobody against them is a scuffle. */
export function dangerFromNumbers(partyInTheFight: number, tiers: readonly ThreatTier[]): DangerStep {
  if (tiers.length === 0) return 1;
  const highest = Math.max(...tiers.map((tier) => TIER_ORDER.indexOf(tier)));
  const outnumbered = partyInTheFight > 0 && tiers.length >= 2 * partyInTheFight;
  return clampStep(STEP_OF_TIER[TIER_ORDER[highest]] + (outnumbered ? 1 : 0));
}

interface Opponent {
  id: string;
  name: string;
  tier: ThreatTier;
}

export interface DangerReading {
  /** What the livery wears: the pin when there is one, the numbers otherwise. */
  step: DangerStep;
  /** What the numbers say, pinned or not. */
  computed: DangerStep;
  pinned: boolean;
  /** Who still stands against the party, in roster order. */
  opposition: Opponent[];
  partyInTheFight: number;
}

type Sides = Pick<PhaseFrame, 'partyTeam' | 'dangerPin'>;

/**
 * A combatant's tier, remembered on the record itself: a roster edit makes a
 * new object only for whoever changed, so one wound re-reads one Codex entry.
 */
const tiers = new WeakMap<Combatant, ThreatTier>();
function tierOf(c: Combatant): ThreatTier {
  const known = tiers.get(c);
  if (known) return known;
  const tier = classifyThreat(projectDefender(c), c.sheet).tier;
  tiers.set(c, tier);
  return tier;
}

export function readDanger(roster: readonly Combatant[], frame: Sides): DangerReading {
  const { party, opposition: against } = sidesOf(roster, frame.partyTeam);
  const opposition = against.map((c) => ({ id: c.id, name: c.name, tier: tierOf(c) }));
  const computed = dangerFromNumbers(
    party.length,
    opposition.map((o) => o.tier),
  );
  const pinned = frame.dangerPin !== null;
  return { step: frame.dangerPin ?? computed, computed, pinned, opposition, partyInTheFight: party.length };
}
